import { NavLink } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ChangePasswordButton } from "@/components/ChangePasswordButton";
import {
  LayoutDashboard, ClipboardList, FileText, CalendarRange,
  Users, Truck, Building2, Route, Boxes, MapPin, Fuel, CalendarClock, Plug, Radar,
  Shield, LogOut, PackageCheck,
} from "lucide-react";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/master-log", label: "Master Log", icon: ClipboardList, testid: "nav-master-log" },
  { to: "/dispatch", label: "Dispatch", icon: Radar, testid: "nav-dispatch" },
  { to: "/work-orders", label: "Work Orders", icon: FileText, testid: "nav-work-orders" },
  { to: "/schedule", label: "Schedule", icon: CalendarRange, testid: "nav-schedule" },
  { to: "/driver-schedules", label: "Driver Schedules", icon: CalendarClock, testid: "nav-driver-schedules" },
  { to: "/deliveries", label: "Deliveries", icon: PackageCheck, testid: "nav-deliveries" },
  { to: "/drivers", label: "Drivers", icon: Users, testid: "nav-drivers" },
  { to: "/assets", label: "Internal Assets", icon: Truck, testid: "nav-assets" },
  { to: "/vendors", label: "Vendors", icon: Building2, testid: "nav-vendors" },
  { to: "/trips", label: "Trip Log", icon: Route, testid: "nav-trips" },
  { to: "/locations", label: "Locations", icon: MapPin, testid: "nav-locations" },
  { to: "/diesel", label: "Diesel Prices", icon: Fuel, testid: "nav-diesel" },
  { to: "/integrations", label: "Integrations", icon: Plug, testid: "nav-integrations" },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  return (
    <div className="flex min-h-screen bg-[#FDFDFD]">
      <aside className="sidebar-noise w-64 flex-shrink-0 fixed inset-y-0 left-0 text-white flex flex-col z-30">
        <div className="px-5 h-16 flex items-center gap-2.5 border-b border-white/10">
          <div className="h-9 w-9 rounded-md overflow-hidden bg-[#0A0A0A] grid place-items-center">
            <img src="/ghost-logo.png" alt="Casper" className="h-9 w-9 object-cover" />
          </div>
          <div className="leading-tight">
            <div className="font-display font-extrabold tracking-tight text-[15px]">GHOST TMS</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Transport Mgmt</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {nav.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-[#0055FF] text-white"
                      : "text-white/60 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <Icon className="h-[18px] w-[18px]" />
                {n.label}
              </NavLink>
            );
          })}
          {user?.role === "admin" && (
            <NavLink to="/users" data-testid="nav-users"
              className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-[#0055FF] text-white" : "text-white/60 hover:text-white hover:bg-white/5"}`}>
              <Shield className="h-[18px] w-[18px]" /> Users &amp; Access
            </NavLink>
          )}
        </nav>
        <div className="px-3 py-3 border-t border-white/10">
          <div className="flex items-center gap-2.5 px-2 py-1.5">
            <div className="h-8 w-8 rounded-full bg-white/10 grid place-items-center text-xs font-bold text-white/80">
              {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold text-white/90 truncate">{user?.name || user?.email}</div>
              <div className="text-[10px] uppercase tracking-wide text-white/40">{user?.role === "admin" ? "Administrator" : "Dispatcher"}</div>
            </div>
            <button data-testid="logout-btn" onClick={logout} title="Sign out"
              className="text-white/50 hover:text-white p-1.5 rounded hover:bg-white/5">
              <LogOut className="h-4 w-4" />
            </button>
            <ChangePasswordButton />
          </div>
        </div>
      </aside>
      <main className="flex-1 ml-64 min-w-0">{children}</main>
    </div>
  );
};

export const PageHeader = ({ title, subtitle, children, noPrint }) => (
  <header className={`sticky top-0 z-20 backdrop-blur-xl bg-white/80 border-b border-border/60 ${noPrint ? "no-print" : ""}`}>
    <div className="px-8 h-16 flex items-center justify-between gap-4">
      <div>
        <h1 className="font-display font-extrabold tracking-tight text-2xl leading-none">{title}</h1>
        {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  </header>
);
